import { DateTimeQuestion } from '../../../../types/questions';

export interface DatePickerBounds {
  minimumDate?: Date;
  maximumDate?: Date;
}

export interface DaysAndHours {
  days: number;
  hours: number;
}

export const convertHoursToDaysAndHours = (hours: number | string): DaysAndHours => {
  const totalHours = typeof hours === 'string' ? parseFloat(hours) : hours;
  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;

  return {
    days,
    hours: remainingHours,
  };
};

const truncateSeconds = (date: Date): Date => {
  date.setSeconds(0, 0);
  return date;
};

export const getDatePickerBounds = (
  question: DateTimeQuestion,
  formAnswerCreatedAt?: Date,
): DatePickerBounds => {
  const {
    dateValidationType,
    rangeCondition,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
    dateMode,
  } = question;

  if (dateValidationType !== 'fixedRange' || !rangeCondition || !formAnswerCreatedAt) {
    return {};
  }

  const formAnswerCreatedAtDate = new Date(formAnswerCreatedAt);

  if (dateMode === 'date') {
    formAnswerCreatedAtDate.setHours(0, 0, 0, 0);
  } else if (dateMode === 'datetime') {
    truncateSeconds(formAnswerCreatedAtDate);
  }

  const datePickerBounds: DatePickerBounds = {};

  switch (rangeCondition) {
    case 'onlyToday':
      if (dateMode === 'time') {
        const truncatedFormAnswerCreatedAtDate = truncateSeconds(formAnswerCreatedAtDate);
        datePickerBounds.minimumDate = truncatedFormAnswerCreatedAtDate;
        datePickerBounds.maximumDate = truncatedFormAnswerCreatedAtDate;
      } else {
        const startOfDay = new Date(formAnswerCreatedAtDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(formAnswerCreatedAtDate);
        endOfDay.setHours(23, 59, 59, 999);
        datePickerBounds.minimumDate = startOfDay;
        datePickerBounds.maximumDate = endOfDay;
      }
      break;

    case 'noFutureDates':
      if (dateMode === 'date') {
        const endOfDay = new Date(formAnswerCreatedAtDate);
        endOfDay.setHours(23, 59, 59, 999);
        datePickerBounds.maximumDate = endOfDay;
      } else if (dateMode === 'time') {
        const startOfDay = new Date(formAnswerCreatedAtDate);
        startOfDay.setHours(0, 0, 0, 0);
        datePickerBounds.minimumDate = startOfDay;
        datePickerBounds.maximumDate = truncateSeconds(new Date(formAnswerCreatedAtDate));
      } else {
        datePickerBounds.maximumDate = truncateSeconds(new Date(formAnswerCreatedAtDate));
      }
      break;

    case 'noPastDates':
      if (dateMode === 'date') {
        const startOfDay = new Date(formAnswerCreatedAtDate);
        startOfDay.setHours(0, 0, 0, 0);
        datePickerBounds.minimumDate = startOfDay;
      } else if (dateMode === 'time') {
        datePickerBounds.minimumDate = truncateSeconds(formAnswerCreatedAtDate);
        const endOfDay = new Date(formAnswerCreatedAtDate);
        endOfDay.setHours(23, 59, 59, 999);
        datePickerBounds.maximumDate = endOfDay;
      } else {
        datePickerBounds.minimumDate = formAnswerCreatedAtDate;
      }
      break;

    case 'after':
      if (offsetHours) {
        const offsetHoursFloat = parseFloat(offsetHours);
        if (!isNaN(offsetHoursFloat) && offsetHoursFloat > 0) {
          if (dateMode === 'date') {
            const days = Math.ceil(offsetHoursFloat / 24);
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setDate(minDate.getDate() + 1);
            minDate.setHours(0, 0, 0, 0);
            datePickerBounds.minimumDate = minDate;

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setDate(maxDate.getDate() + days);
            maxDate.setHours(23, 59, 59, 999);
            datePickerBounds.maximumDate = maxDate;
          } else if (dateMode === 'time') {
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setMinutes(minDate.getMinutes() + 1);
            datePickerBounds.minimumDate = truncateSeconds(minDate);

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setHours(maxDate.getHours() + offsetHoursFloat);

            const endOfDay = new Date(formAnswerCreatedAtDate);
            endOfDay.setHours(23, 59, 59, 999);
            if (maxDate > endOfDay) {
              maxDate.setTime(endOfDay.getTime());
            }
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          } else {
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setMinutes(minDate.getMinutes() + 1);
            datePickerBounds.minimumDate = truncateSeconds(minDate);

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setTime(maxDate.getTime() + offsetHoursFloat * 60 * 60 * 1000);
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          }
        }
      }
      break;

    case 'afterOrEqual':
      if (offsetHours) {
        const offsetHoursFloat = parseFloat(offsetHours);
        if (!isNaN(offsetHoursFloat) && offsetHoursFloat >= 0) {
          const minDate = new Date(formAnswerCreatedAtDate);
          if (dateMode === 'date') {
            minDate.setHours(0, 0, 0, 0);
          } else {
            truncateSeconds(minDate);
          }
          datePickerBounds.minimumDate = minDate;

          const maxDate = new Date(formAnswerCreatedAtDate);
          maxDate.setHours(maxDate.getHours() + offsetHoursFloat);

          if (dateMode === 'date') {
            maxDate.setHours(23, 59, 59, 999);
          } else if (dateMode === 'time') {
            const endOfDay = new Date(formAnswerCreatedAtDate);
            endOfDay.setHours(23, 59, 59, 999);
            if (maxDate > endOfDay) {
              maxDate.setTime(endOfDay.getTime());
            }
            truncateSeconds(maxDate);
          } else {
            truncateSeconds(maxDate);
          }
          datePickerBounds.maximumDate = maxDate;
        }
      }
      break;

    case 'before':
      if (offsetHours) {
        const offsetHoursFloat = parseFloat(offsetHours);
        if (!isNaN(offsetHoursFloat) && offsetHoursFloat > 0) {
          if (dateMode === 'date') {
            const days = Math.ceil(offsetHoursFloat / 24);
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setDate(minDate.getDate() - days);
            minDate.setHours(0, 0, 0, 0);
            datePickerBounds.minimumDate = minDate;

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setDate(maxDate.getDate() - 1);
            maxDate.setHours(23, 59, 59, 999);
            datePickerBounds.maximumDate = maxDate;
          } else if (dateMode === 'time') {
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setHours(minDate.getHours() - offsetHoursFloat);

            const startOfDay = new Date(formAnswerCreatedAtDate);
            startOfDay.setHours(0, 0, 0, 0);
            if (minDate < startOfDay) {
              minDate.setTime(startOfDay.getTime());
            }
            datePickerBounds.minimumDate = truncateSeconds(minDate);

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setMinutes(maxDate.getMinutes() - 1);
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          } else {
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setTime(minDate.getTime() - offsetHoursFloat * 60 * 60 * 1000);
            datePickerBounds.minimumDate = truncateSeconds(minDate);

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setMinutes(maxDate.getMinutes() - 1);
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          }
        }
      }
      break;

    case 'beforeOrEqual':
      if (offsetHours) {
        const offsetHoursFloat = parseFloat(offsetHours);
        if (!isNaN(offsetHoursFloat) && offsetHoursFloat >= 0) {
          const minDate = new Date(formAnswerCreatedAtDate);
          minDate.setHours(minDate.getHours() - offsetHoursFloat);

          if (dateMode === 'date') {
            minDate.setHours(0, 0, 0, 0);
            datePickerBounds.minimumDate = minDate;
          } else if (dateMode === 'time') {
            const startOfDay = new Date(formAnswerCreatedAtDate);
            startOfDay.setHours(0, 0, 0, 0);
            if (minDate < startOfDay) {
              minDate.setTime(startOfDay.getTime());
            }
            datePickerBounds.minimumDate = truncateSeconds(minDate);
          } else {
            datePickerBounds.minimumDate = truncateSeconds(minDate);
          }

          const maxDate = new Date(formAnswerCreatedAtDate);
          if (dateMode === 'date') {
            maxDate.setHours(23, 59, 59, 999);
            datePickerBounds.maximumDate = maxDate;
          } else {
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          }
        }
      }
      break;

    case 'between':
      if (offsetHoursToLeft && offsetHoursToRight) {
        const offsetHoursToLeftFloat = parseFloat(offsetHoursToLeft);
        const offsetHoursToRightFloat = parseFloat(offsetHoursToRight);

        if (!isNaN(offsetHoursToLeftFloat) && !isNaN(offsetHoursToRightFloat)) {
          const minDate = new Date(formAnswerCreatedAtDate);
          minDate.setHours(minDate.getHours() - offsetHoursToLeftFloat);

          if (dateMode === 'date') {
            minDate.setHours(0, 0, 0, 0);
          } else if (dateMode === 'time') {
            const startOfDay = new Date(formAnswerCreatedAtDate);
            startOfDay.setHours(0, 0, 0, 0);
            if (minDate < startOfDay) {
              minDate.setTime(startOfDay.getTime());
            }
            truncateSeconds(minDate);
          } else {
            truncateSeconds(minDate);
          }
          datePickerBounds.minimumDate = minDate;

          const maxDate = new Date(formAnswerCreatedAtDate);
          maxDate.setHours(maxDate.getHours() + offsetHoursToRightFloat);

          if (dateMode === 'date') {
            maxDate.setHours(23, 59, 59, 999);
          } else if (dateMode === 'time') {
            const endOfDay = new Date(formAnswerCreatedAtDate);
            endOfDay.setHours(23, 59, 59, 999);
            if (maxDate > endOfDay) {
              maxDate.setTime(endOfDay.getTime());
            }
            truncateSeconds(maxDate);
          } else {
            truncateSeconds(maxDate);
          }
          datePickerBounds.maximumDate = maxDate;
        }
      }
      break;

    default:
      break;
  }

  return datePickerBounds;
};
